import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import {
  useSalary,
  useInflation,
  useUpsertSalary,
  useDeleteSalary,
  computeSalaryGrowth,
  realGrowth,
  type SalaryEntry,
} from "@/lib/queries";
import { SALARY_ENTRY_TYPES, type SalaryEntryType } from "@/lib/categories";
import {
  formatNok,
  formatNumber,
  formatPercent,
  formatDate,
  formatMoneyEdit,
  formatSignedDelta,
} from "@/lib/format";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Trash2, X, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { InfoTooltip } from "@/components/InfoTooltip";
import { FreeLimitNudge } from "@/components/FreeLimitNudge";
import { FREE_VISIBLE_LIMIT, rankSalaryForGate, topNIds, useIsFree } from "@/lib/free-limits";
import {
  BlurredGateWrapper,
  GateStatsNote,
  UpgradeGateRow,
  UpgradeLockPanel,
} from "@/components/UpgradeGate";

export const Route = createFileRoute("/_authenticated/salary")({
  head: () => ({
    meta: [
      { title: "Income — VISIKT" },
      {
        name: "description",
        content: "Track salary growth, bonuses, and real purchasing power against CPI inflation.",
      },
    ],
  }),
  validateSearch: (s): { new?: number } => ({ new: s.new ? Number(s.new) : undefined }),
  component: SalaryPage,
});

import {
  moneyInputProps,
  sanitizeMoney,
  normalizeMoneyForZod as normalizeMoney,
} from "@/lib/money-input";

function makeSalarySchema(t: (k: string) => string) {
  const moneyRequired = z.preprocess(
    normalizeMoney,
    z
      .number({ invalid_type_error: t("salary.dialog.errInvalidNumber") })
      .min(0, t("salary.dialog.errNegative"))
      .max(100000000, t("salary.dialog.errTooLarge")),
  );
  const moneyOptional = z.preprocess(
    normalizeMoney,
    z
      .number({ invalid_type_error: t("salary.dialog.errInvalidNumber") })
      .min(0, t("salary.dialog.errNegative"))
      .max(100000000, t("salary.dialog.errTooLarge"))
      .nullable()
      .optional(),
  );
  const todayIsoStr = new Date().toISOString().slice(0, 10);
  return z.object({
    gross_annual_nok: moneyRequired,
    effective_date: z
      .string()
      .min(1, t("salary.dialog.errDateRequired"))
      .refine((s) => s <= todayIsoStr, t("salary.dialog.errFutureDate")),
    bonus_nok: moneyOptional,
    vacation_pay_nok: moneyOptional,
    benefits_nok: moneyOptional,
    other_income_nok: moneyOptional,
    notes: z.string().max(500).optional().nullable(),
    entry_type: z.enum(
      SALARY_ENTRY_TYPES.map((s) => s.value) as [SalaryEntryType, ...SalaryEntryType[]],
    ),
  });
}

function SalaryPage() {
  const { t } = useTranslation();
  const sal = useSalary();
  const infl = useInflation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/salary" }) as { new?: number };
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SalaryEntry | null>(null);
  const [expandedYear, setExpandedYear] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [pageHeights, setPageHeights] = useState<number[]>([]);
  const FALLBACK_PAGE_HEIGHT = 400;
  const containerHeight = pageHeights.length
    ? Math.max(FALLBACK_PAGE_HEIGHT, ...pageHeights)
    : FALLBACK_PAGE_HEIGHT;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 });
    setCurrentPage(0);
  }, [sal.data]);

  useEffect(() => {
    if (search?.new) {
      setCreating(true);
      navigate({ to: "/salary", replace: true, search: {} as never });
    }
  }, [search?.new, navigate]);

  const isFree = useIsFree();
  const allEntries = useMemo(() => sal.data ?? [], [sal.data]);
  const rankedEntries = useMemo(() => rankSalaryForGate(allEntries), [allEntries]);
  const gateIds = useMemo(
    () => (isFree ? topNIds(rankedEntries, FREE_VISIBLE_LIMIT) : null),
    [isFree, rankedEntries],
  );
  const hiddenCount = isFree ? Math.max(0, allEntries.length - FREE_VISIBLE_LIMIT) : 0;
  // For Free users, all salary-derived stats are computed from the top-3
  // most-recent entries only (the same set that shows unblurred below).
  const entriesForStats = useMemo(
    () => (gateIds ? allEntries.filter((e) => gateIds.has(e.id)) : allEntries),
    [allEntries, gateIds],
  );
  const rowsAll = useMemo(
    () => computeSalaryGrowth(entriesForStats, infl.data ?? []),
    [entriesForStats, infl.data],
  );
  // CPI/inflation for Free is restricted to the current calendar year only.
  const currentYear = new Date().getFullYear();
  const rows = useMemo(
    () => (isFree ? rowsAll.filter((r) => r.year === currentYear) : rowsAll),
    [isFree, rowsAll, currentYear],
  );
  const latest = rows[rows.length - 1];
  const first = rows[0];
  const entries = rankedEntries;
  // Measure every page independently so pagination survives variable-height rows.
  useLayoutEffect(() => {
    const els = pageRefs.current.filter((el): el is HTMLDivElement => !!el);
    if (els.length === 0) return;
    const measure = () => {
      setPageHeights((prev) => {
        const next = els.map((el) => el.getBoundingClientRect().height);
        if (prev.length === next.length && prev.every((h, i) => Math.abs(h - next[i]) < 0.5)) {
          return prev;
        }
        return next;
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [entries.length, pageCount]);
  const scrollToPage = (i: number) => {
    const el = scrollRef.current;
    const target = pageRefs.current[i];
    if (!el || !target) return;
    el.scrollTo({ top: target.offsetTop, behavior: "smooth" });
  };

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const selectedRow =
    selectedYear != null ? (rows.find((r) => r.year === selectedYear) ?? null) : null;

  // Aggregate view (no year selected): total growth/CPI/real from first to latest year
  const aggregate = (() => {
    if (!first || !latest || rows.length < 2) return null;
    const totalGrowth =
      first.salary > 0 ? ((latest.salary - first.salary) / first.salary) * 100 : null;
    let cpiFactor = 1;
    let hasAny = false;
    let usedProvisional = false;
    for (let i = 1; i < rows.length; i++) {
      const c = rows[i].inflationPct;
      if (c != null) {
        cpiFactor *= 1 + c / 100;
        hasAny = true;
      }
      if (rows[i].inflationKind === "tolvmanedersendring") usedProvisional = true;
    }
    const totalCpi = hasAny ? (cpiFactor - 1) * 100 : null;
    const totalReal =
      totalGrowth != null && totalCpi != null ? realGrowth(totalGrowth, totalCpi) : null;
    return {
      salary: latest.salary,
      growthPct: totalGrowth,
      inflationPct: totalCpi,
      realGrowthPct: totalReal,
      inflationKind: usedProvisional ? ("tolvmanedersendring" as const) : ("arsvekst" as const),
    };
  })();

  const display = selectedRow ?? aggregate ?? latest;
  const isAggregate = !selectedRow && !!aggregate;
  const spanLabel = isAggregate && first && latest ? `${first.year}–${latest.year}` : null;

  return (
    <AppShell>
      {allEntries.length < 3 && (
        <p className="text-[12px] leading-snug text-muted-foreground -mt-2 mb-4 animate-entry">
          {t("salary.firstRunHint")}
        </p>
      )}
      <section className="animate-entry">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
          {t("salary.eyebrow")}
          {selectedRow ? ` · ${selectedRow.year}` : spanLabel ? ` · ${spanLabel}` : ""}
        </p>
        <h1 className="text-2xl font-display font-bold tracking-tight mb-6">
          {display ? formatNok(display.salary) : t("salary.none")}
        </h1>

        {display && (
          <div className="grid grid-cols-3 gap-3 border-y border-border py-4">
            <Stat
              label={
                <span className="inline-flex items-center gap-1">
                  {t("salary.growth")}
                  <InfoTooltip text={t("salary.growthHelp")} label={t("salary.growth")} />
                </span>
              }
              value={formatPercent(display.growthPct, { signed: true })}
              tone={tone(display.growthPct)}
            />
            <Stat
              label={
                <span className="inline-flex items-center gap-1">
                  {t("salary.cpi")}
                  <InfoTooltip text={t("salary.cpiHelp")} label={t("salary.cpi")} />
                </span>
              }
              value={display.inflationPct != null ? `${display.inflationPct.toFixed(1)}%` : "—"}
              badge={
                display.inflationKind === "tolvmanedersendring"
                  ? t("dashboard.cpiProvisional")
                  : undefined
              }
              badgeTooltip={
                display.inflationKind === "tolvmanedersendring"
                  ? t("salary.cpiProvisionalHelp")
                  : undefined
              }
            />
            <Stat
              label={
                <span className="inline-flex items-center gap-1">
                  {t("salary.real")}
                  <InfoTooltip text={t("salary.realHelp")} label={t("salary.real")} />
                </span>
              }
              value={formatPercent(display.realGrowthPct, { signed: true })}
              tone={tone(display.realGrowthPct)}
            />
          </div>
        )}
        {isFree && hiddenCount > 0 && <GateStatsNote />}
      </section>

      {rows.length > 0 && (
        <section className="animate-entry [animation-delay:100ms]">
          <h2 className="font-display font-bold text-sm tracking-tight mb-4">
            {t("salary.realGrowth")}
          </h2>
          <div className="space-y-3">
            {rows
              .slice()
              .reverse()
              .map((r) => {
                const isSelected = selectedRow?.year === r.year;
                return (
                  <button
                    key={r.year}
                    type="button"
                    onClick={() => setSelectedYear(r.year === selectedYear ? null : r.year)}
                    className={
                      "w-full flex items-center gap-4 text-left transition-opacity " +
                      (isSelected ? "" : "opacity-70 hover:opacity-100")
                    }
                  >
                    <span className="font-mono text-[10px] text-muted-foreground w-10 shrink-0">
                      {r.year}
                    </span>
                    <div className="flex-1">
                      <BarRow growth={r.growthPct} inflation={r.inflationPct} />
                    </div>
                    <span
                      className={
                        "font-mono text-[11px] tabular-nums w-14 text-right shrink-0 " +
                        toneClass(r.realGrowthPct)
                      }
                    >
                      {r.realGrowthPct != null
                        ? formatPercent(r.realGrowthPct, { signed: true })
                        : "—"}
                    </span>
                  </button>
                );
              })}
          </div>
          <div className="mt-4 flex gap-4 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-2 bg-accent inline-block" /> {t("salary.legendGrowth")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 bg-foreground/20 inline-block" /> {t("salary.legendCpi")}
            </span>
          </div>
          {isFree && rowsAll.length > rows.length && (
            <div className="mt-4">
              <UpgradeLockPanel title={t("gate.cpiTitle")} body={t("gate.cpiBody")} />
            </div>
          )}
        </section>
      )}

      {isFree ? (
        <section className="animate-entry [animation-delay:150ms]">
          <UpgradeLockPanel title={t("gate.statsLockedTitle")} body={t("gate.statsLockedBody")} />
        </section>
      ) : (
        rows.length > 0 && <StatsSection rows={rows} />
      )}

      <FreeLimitNudge
        kind="salary"
        count={new Set(allEntries.map((e) => new Date(e.effective_date).getFullYear())).size}
        limit={3}
      />

      <section className="animate-entry [animation-delay:200ms]">
        <div className="flex items-end justify-between mb-3 gap-3">
          <h2 className="text-lg font-display font-bold tracking-tight">{t("salary.history")}</h2>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("salary.total", { n: allEntries.length })}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => scrollToPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  aria-label={t("common.previous")}
                  className="size-7 grid place-items-center rounded-full border border-border/30 bg-card text-foreground/80 hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronUp className="size-4" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollToPage(Math.min(pageCount - 1, currentPage + 1))}
                  disabled={currentPage >= pageCount - 1}
                  aria-label={t("common.next")}
                  className="size-7 grid place-items-center rounded-full border border-border/30 bg-card text-foreground/80 hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronDown className="size-4" strokeWidth={1.75} />
                </button>
              </div>
            )}
          </div>
        </div>
        {sal.isLoading ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("salary.emptyHistory")}</p>
        ) : (
          <div className="space-y-3">
            <div
              ref={scrollRef}
              onScroll={(e) => {
                const top = (e.target as HTMLDivElement).scrollTop;
                const offsets = pageRefs.current.map((el) => (el ? el.offsetTop : Infinity));
                let idx = 0;
                let bestDiff = Infinity;
                offsets.forEach((o, i) => {
                  const d = Math.abs(o - top);
                  if (d < bestDiff) {
                    bestDiff = d;
                    idx = i;
                  }
                });
                if (idx !== currentPage) setCurrentPage(idx);
              }}
              className="overflow-y-auto snap-y snap-mandatory overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              style={{
                height: containerHeight,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {(() => {
                const items: { key: string; node: React.ReactNode }[] = [];
                let ctaInserted = false;
                entries.forEach((e, idx) => {
                  const year = new Date(e.effective_date).getFullYear();
                  const row = rows.find((r) => r.year === year);
                  const prev = entries[idx + 1];
                  const prevGated = prev != null && gateIds != null && !gateIds.has(prev.id);
                  const showDeltas = prev != null && !prevGated;
                  const prevVal = prev ? Number(prev.gross_annual_nok) : null;
                  const curVal = Number(e.gross_annual_nok);
                  // Gate percentages the same as kroner deltas — a value derived
                  // from a hidden previous entry would leak that entry via
                  // arithmetic (prev = cur / (1 + growth/100)).
                  const growthPct =
                    showDeltas && prevVal != null && prevVal > 0
                      ? ((curVal - prevVal) / prevVal) * 100
                      : null;
                  const cpiPct = row?.inflationPct ?? null;
                  // Ratio formula — must match the chart's calculation.
                  const realPct =
                    growthPct != null && cpiPct != null ? realGrowth(growthPct, cpiPct) : null;
                  const gated = gateIds != null && !gateIds.has(e.id);
                  const extra = e as SalaryEntry & {
                    vacation_pay_nok?: number | null;
                    benefits_nok?: number | null;
                    other_income_nok?: number | null;
                  };
                  const bonusCur = Number(e.bonus_nok ?? 0);
                  const vacCur = Number(extra.vacation_pay_nok ?? 0);
                  const otherCur = Number(extra.other_income_nok ?? 0);
                  const benCur = Number(extra.benefits_nok ?? 0);
                  const prevExtra = prev as
                    | (SalaryEntry & {
                        vacation_pay_nok?: number | null;
                        benefits_nok?: number | null;
                        other_income_nok?: number | null;
                      })
                    | undefined;
                  const bonusPrev = showDeltas ? Number(prev!.bonus_nok ?? 0) : 0;
                  const vacPrev = showDeltas ? Number(prevExtra!.vacation_pay_nok ?? 0) : 0;
                  const otherPrev = showDeltas ? Number(prevExtra!.other_income_nok ?? 0) : 0;
                  const benPrev = showDeltas ? Number(prevExtra!.benefits_nok ?? 0) : 0;

                  const salaryDelta = showDeltas ? curVal - (prevVal ?? 0) : null;
                  const bonusDelta = showDeltas ? bonusCur - bonusPrev : null;
                  const vacDelta = showDeltas ? vacCur - vacPrev : null;
                  const otherDelta = showDeltas ? otherCur - otherPrev : null;
                  const benDelta = showDeltas ? benCur - benPrev : null;
                  // Vacation pay (feriepenger) replaces salary during holiday
                  // rather than adding to it, so it's shown separately below
                  // the total — treated like benefits (contextual, not additive).
                  const totalCur = curVal + bonusCur + otherCur;
                  const totalPrev = showDeltas ? (prevVal ?? 0) + bonusPrev + otherPrev : 0;
                  const totalDelta = showDeltas ? totalCur - totalPrev : null;

                  const prevYear = prev ? new Date(prev.effective_date).getFullYear() : null;
                  const yearGap =
                    showDeltas && prevYear != null && year - prevYear > 1 ? prevYear : null;

                  const isExpanded = expandedYear === year && !gated;
                  const panelId = `salary-detail-${e.id}`;

                  type CompLine = {
                    label: string;
                    amount: number;
                    delta: number | null;
                  };
                  const componentLines: CompLine[] = [
                    { label: t("salary.fixedSalary"), amount: curVal, delta: salaryDelta },
                  ];
                  // A component line renders only when its current amount is
                  // non-zero OR its year-over-year change is non-zero. Using
                  // rounded comparison keeps float noise from resurrecting a
                  // phantom line, and using `!== 0` (not `!= null`) means a
                  // 0→0 delta correctly hides the line while a positive→0
                  // fall still renders (delta is non-zero).
                  const isNonZero = (n: number | null | undefined) =>
                    n != null && Math.round(n) !== 0;
                  const pushComp = (label: string, amount: number, delta: number | null) => {
                    if (isNonZero(amount) || isNonZero(delta))
                      componentLines.push({ label, amount, delta });
                  };
                  pushComp(t("salary.bonusLabel"), bonusCur, bonusDelta);
                  pushComp(t("salary.otherIncomeLabel"), otherCur, otherDelta);

                  const vacationVisible = isNonZero(vacCur) || isNonZero(vacDelta);
                  const benefitsVisible = isNonZero(benCur) || isNonZero(benDelta);

                  const realStr = realPct != null ? formatPercent(realPct, { signed: true }) : "—";

                  const collapsedLine = (
                    <div className="grid grid-cols-[2.375rem_minmax(0,1fr)_auto] items-baseline gap-3 px-3 py-3">
                      <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
                        {year}
                      </span>
                      <span className="font-mono text-sm tabular-nums text-foreground">
                        {formatNumber(curVal)}
                      </span>
                      <span
                        className={
                          "font-mono text-sm tabular-nums text-right " +
                          (realPct == null ? "text-muted-foreground" : toneClass(realPct))
                        }
                      >
                        {realStr}
                      </span>
                    </div>
                  );

                  const detail = (
                    <div
                      id={panelId}
                      role="region"
                      aria-label={t("salary.expandRow", { year })}
                      className="px-3 pb-3 pt-2 border-t border-border/40 animate-entry"
                    >
                      <dl className="space-y-1">
                        {componentLines.map((c, i) => {
                          const deltaStr = c.delta != null ? formatSignedDelta(c.delta) : null;
                          return (
                            <div
                              key={c.label + i}
                              className="grid grid-cols-[minmax(0,1fr)_auto_5.5rem] items-baseline gap-2"
                            >
                              <dt className="text-[11px] text-muted-foreground truncate">
                                {c.label}
                              </dt>
                              <dd className="font-mono text-[12px] tabular-nums text-right">
                                {formatNumber(c.amount)}
                              </dd>
                              <dd
                                className={
                                  "font-mono text-[11px] tabular-nums text-right " +
                                  toneClass(c.delta)
                                }
                              >
                                {deltaStr ?? ""}
                                {i === 0 && yearGap != null && deltaStr && (
                                  <span className="ml-1 text-muted-foreground">
                                    {t("salary.sinceYear", { year: yearGap })}
                                  </span>
                                )}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>

                      {componentLines.length > 1 && (
                        <div className="mt-2 pt-1.5 border-t border-border/40 grid grid-cols-[minmax(0,1fr)_auto_5.5rem] items-baseline gap-2">
                          <div className="text-[11px] font-semibold">{t("salary.totalPaid")}</div>
                          <div className="font-mono text-[13px] font-semibold tabular-nums text-right">
                            {formatNok(totalCur)}
                          </div>
                          <div
                            className={
                              "font-mono text-[11px] tabular-nums text-right " +
                              toneClass(totalDelta)
                            }
                          >
                            {totalDelta != null ? (formatSignedDelta(totalDelta) ?? "") : ""}
                          </div>
                        </div>
                      )}

                      {vacationVisible && (
                        <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto_5.5rem] items-baseline gap-2 text-muted-foreground">
                          <div className="text-[11px] inline-flex items-center gap-1 min-w-0 truncate">
                            <span aria-hidden="true">+</span>
                            <span className="truncate">{t("salary.vacationPayLabel")}</span>
                            <InfoTooltip
                              text={t("salary.vacationReplacesSalaryHelp")}
                              label={t("salary.vacationReplacesSalary")}
                            />
                          </div>
                          <div className="font-mono text-[11px] tabular-nums text-right">
                            {formatNumber(vacCur)}
                          </div>
                          <div
                            className={
                              "font-mono text-[11px] tabular-nums text-right " + toneClass(vacDelta)
                            }
                          >
                            {vacDelta != null ? (formatSignedDelta(vacDelta) ?? "") : ""}
                          </div>
                        </div>
                      )}

                      {benefitsVisible && (
                        <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto_5.5rem] items-baseline gap-2 text-muted-foreground">
                          <div className="text-[11px] inline-flex items-center gap-1 min-w-0 truncate">
                            <span aria-hidden="true">+</span>
                            <span className="truncate">{t("salary.benefitsLabel")}</span>
                            <InfoTooltip
                              text={t("salary.benefitsNotPaidOutHelp")}
                              label={t("salary.benefitsNotPaidOut")}
                            />
                          </div>
                          <div className="font-mono text-[11px] tabular-nums text-right">
                            {formatNumber(benCur)}
                          </div>
                          <div
                            className={
                              "font-mono text-[11px] tabular-nums text-right " + toneClass(benDelta)
                            }
                          >
                            {benDelta != null ? (formatSignedDelta(benDelta) ?? "") : ""}
                          </div>
                        </div>
                      )}

                      <div className="mt-3 pt-2 border-t border-border/40 flex items-center justify-between gap-3">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex flex-wrap gap-3">
                          <span>
                            {t("salary.nominalShort")}{" "}
                            <span className={"tabular-nums " + toneClass(growthPct)}>
                              {growthPct != null ? formatPercent(growthPct, { signed: true }) : "—"}
                            </span>
                          </span>
                          <span>
                            {t("salary.inflationShort")}{" "}
                            <span className="tabular-nums text-foreground/70">
                              {cpiPct != null ? `${cpiPct.toFixed(1)}%` : "—"}
                            </span>
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setEditing(e);
                          }}
                          className="font-mono text-[10px] uppercase tracking-widest text-accent hover:text-accent/80 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded px-1"
                        >
                          {t("common.edit")}
                        </button>
                      </div>
                    </div>
                  );

                  const card = (
                    <article
                      className={cn(
                        "rounded bg-card border transition-colors",
                        isExpanded ? "border-accent/60" : "border-border/30",
                      )}
                    >
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                        aria-label={
                          isExpanded
                            ? t("salary.collapseRow", { year })
                            : t("salary.expandRow", { year })
                        }
                        onClick={(ev) => {
                          const next = isExpanded ? null : year;
                          setExpandedYear(next);
                          if (next != null) {
                            const li = ev.currentTarget.closest("li");
                            requestAnimationFrame(() => {
                              li?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                            });
                          }
                        }}
                        className="w-full text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
                      >
                        {collapsedLine}
                      </button>
                      {isExpanded && detail}
                    </article>
                  );

                  if (gated) {
                    items.push({
                      key: e.id,
                      node: <BlurredGateWrapper>{card}</BlurredGateWrapper>,
                    });
                    if (!ctaInserted && hiddenCount > 0) {
                      items.push({
                        key: "__gate_cta__",
                        node: <UpgradeGateRow hiddenCount={hiddenCount} kind="salary" />,
                      });
                      ctaInserted = true;
                    }
                  } else {
                    items.push({ key: e.id, node: card });
                  }
                });

                const PAGE_SIZE = 6;
                const pages: { key: string; node: React.ReactNode }[][] = [];
                for (let i = 0; i < items.length; i += PAGE_SIZE) {
                  pages.push(items.slice(i, i + PAGE_SIZE));
                }
                if (pages.length !== pageCount) {
                  queueMicrotask(() => setPageCount(pages.length));
                }
                pageRefs.current.length = pages.length;

                return pages.map((page, idx) => (
                  <div
                    key={idx}
                    ref={(el) => {
                      pageRefs.current[idx] = el;
                    }}
                    className={cn("snap-start", idx < pages.length - 1 && "pb-2")}
                  >
                    <ul className="space-y-2">
                      {page.map((item) => (
                        <li key={item.key}>{item.node}</li>
                      ))}
                    </ul>
                  </div>
                ));
              })()}
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center">
              {t("salary.realChangeCaption")}
            </p>

            {pageCount > 1 && (
              <div className="flex items-center justify-center gap-1.5">
                {Array.from({ length: pageCount }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => scrollToPage(i)}
                    className={cn(
                      "size-2 rounded-full transition-colors",
                      currentPage === i
                        ? "bg-foreground"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50",
                    )}
                    aria-label={t("salary.pageIndicator", { current: i + 1, total: pageCount })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {(creating || editing) && (
        <SalaryDialog
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </AppShell>
  );
}

function tone(v: number | null | undefined): "pos" | "neg" | undefined {
  if (v == null) return undefined;
  return v >= 0 ? "pos" : "neg";
}
function toneClass(v: number | null | undefined) {
  if (v == null) return "text-muted-foreground";
  return v >= 0 ? "text-[color:var(--color-positive)]" : "text-[color:var(--color-negative)]";
}

function Stat({
  label,
  value,
  tone,
  badge,
  badgeTooltip,
}: {
  label: ReactNode;
  value: string;
  tone?: "pos" | "neg";
  badge?: string;
  badgeTooltip?: string;
}) {
  return (
    <div>
      <div className="font-mono text-[9px] text-muted-foreground uppercase mb-1">{label}</div>
      <div
        className={
          "text-base font-display font-bold tabular-nums flex items-baseline gap-1 " +
          (tone === "pos"
            ? "text-[color:var(--color-positive)]"
            : tone === "neg"
              ? "text-[color:var(--color-negative)]"
              : "")
        }
      >
        {value}
        {badge && (
          <span className="inline-flex items-center gap-1 text-[8px] font-mono font-normal normal-case text-muted-foreground border border-border rounded px-1 py-0.5">
            {badge}
            {badgeTooltip && <InfoTooltip text={badgeTooltip} label={badge} />}
          </span>
        )}
      </div>
    </div>
  );
}

function BarRow({ growth, inflation }: { growth: number | null; inflation: number | null }) {
  const max = Math.max(Math.abs(growth ?? 0), Math.abs(inflation ?? 0), 4);
  return (
    <div className="space-y-1">
      <div className="h-1.5 bg-foreground/5">
        <div
          className="h-full bg-accent"
          style={{ width: `${growth != null ? (Math.max(growth, 0) / max) * 100 : 0}%` }}
        />
      </div>
      <div className="h-1.5 bg-foreground/5">
        <div
          className="h-full bg-foreground/20"
          style={{ width: `${inflation != null ? (Math.max(inflation, 0) / max) * 100 : 0}%` }}
        />
      </div>
    </div>
  );
}

function SalaryDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: SalaryEntry | null;
  onClose: () => void;
  onSaved?: (wasNew: boolean) => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertSalary();
  const del = useDeleteSalary();
  const isEditing = !!initial;
  const todayIso = new Date().toISOString().slice(0, 10);
  const initialExtra = initial as
    | (SalaryEntry & {
        vacation_pay_nok?: number | null;
        benefits_nok?: number | null;
        other_income_nok?: number | null;
      })
    | null;
  const [gross, setGross] = useState(initial ? formatMoneyEdit(initial.gross_annual_nok) : "");
  const [date, setDate] = useState(initial?.effective_date ?? todayIso);
  const [bonus, setBonus] = useState(initial?.bonus_nok ? formatMoneyEdit(initial.bonus_nok) : "");
  const [vacation, setVacation] = useState(
    initialExtra?.vacation_pay_nok != null ? formatMoneyEdit(initialExtra.vacation_pay_nok) : "",
  );
  const [benefits, setBenefits] = useState(
    initialExtra?.benefits_nok != null ? formatMoneyEdit(initialExtra.benefits_nok) : "",
  );
  const [other, setOther] = useState(
    initialExtra?.other_income_nok != null ? formatMoneyEdit(initialExtra.other_income_nok) : "",
  );
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [entryType, setEntryType] = useState<SalaryEntryType>(
    (initial?.entry_type as SalaryEntryType) || "salary",
  );
  const hasExtras =
    !!initial?.bonus_nok ||
    initialExtra?.vacation_pay_nok != null ||
    initialExtra?.benefits_nok != null ||
    initialExtra?.other_income_nok != null;
  const [extraOpen, setExtraOpen] = useState(hasExtras);
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Smart date default: on new entries, if the user only changes the year and
  // hasn't manually edited day/month, snap to Dec 31 of past years or today for
  // the current year. Editing an existing entry disables the logic entirely.
  const manualDayMonthRef = useRef<boolean>(isEditing);

  function handleDateChange(next: string) {
    if (!next || isEditing) {
      setDate(next);
      return;
    }
    const [ny, nm, nd] = next.split("-");
    const [oy, om, od] = date.split("-");
    if (nm !== om || nd !== od) {
      manualDayMonthRef.current = true;
      setDate(next);
      return;
    }
    if (ny !== oy && !manualDayMonthRef.current) {
      const currentYear = new Date().getFullYear();
      const yearNum = Number(ny);
      if (yearNum < currentYear) {
        setDate(`${ny}-12-31`);
        return;
      }
      if (yearNum === currentYear) {
        setDate(todayIso);
        return;
      }
    }
    setDate(next);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const schema = makeSalarySchema(t);
    const parsed = schema.safeParse({
      gross_annual_nok: gross,
      effective_date: date,
      bonus_nok: bonus === "" ? null : bonus,
      vacation_pay_nok: vacation === "" ? null : vacation,
      benefits_nok: benefits === "" ? null : benefits,
      other_income_nok: other === "" ? null : other,
      notes: notes || null,
      entry_type: entryType,
    });
    if (!parsed.success) {
      const msg = parsed.error.issues[0].message;
      setFieldError(msg);
      toast.error(msg);
      return;
    }
    setFieldError(null);
    try {
      await upsert.mutateAsync({ id: initial?.id, ...parsed.data });
      toast.success(initial ? t("subs.updated") : t("subs.added"));
      const wasNew = !initial;
      if (onSaved) onSaved(wasNew);
      else onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("common.somethingWrong"));
    }
  }

  async function handleDelete() {
    if (!initial) return;
    if (!confirm(t("salary.confirmDelete"))) return;
    await del.mutateAsync(initial.id);
    toast.success(t("subs.deleted"));
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-background border-t sm:border border-border sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto animate-entry"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              {t("common.nav.salary")}
            </p>
            <h2 className="text-xl font-display font-bold tracking-tight">
              {initial ? t("salary.dialog.titleEdit") : t("salary.dialog.titleNew")}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="size-8 grid place-items-center text-muted-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label={t("salaryExtra.type")}>
            <div className="grid grid-cols-4 gap-1 p-1 border border-border rounded-md">
              {SALARY_ENTRY_TYPES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setEntryType(s.value)}
                  className={
                    "py-1.5 text-[10px] font-mono uppercase tracking-widest rounded transition-colors " +
                    (entryType === s.value
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {t(
                    `salaryExtra.type${s.value.charAt(0).toUpperCase() + s.value.slice(1)}` as const,
                  )}
                </button>
              ))}
            </div>
          </Field>
          <Field label={t("salary.dialog.gross")}>
            <input
              {...moneyInputProps}
              className="input-base"
              required
              placeholder="650 000"
              value={gross}
              onChange={(e) => setGross(sanitizeMoney(e.target.value))}
            />
          </Field>
          <Field label={t("salary.dialog.effective")}>
            <input
              className="input-base"
              type="date"
              required
              max={todayIso}
              value={date}
              onChange={(e) => handleDateChange(e.target.value)}
            />
          </Field>

          <div className="border-t border-border/40 pt-3">
            <button
              type="button"
              onClick={() => setExtraOpen((v) => !v)}
              aria-expanded={extraOpen}
              aria-controls="extra-comp-panel"
              className="w-full flex items-center justify-between text-left"
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t("salary.dialog.extraTitle")}
              </span>
              <ChevronRight
                className={cn(
                  "size-4 text-muted-foreground transition-transform",
                  extraOpen && "rotate-90",
                )}
                strokeWidth={1.5}
              />
            </button>
            {extraOpen && (
              <div id="extra-comp-panel" className="mt-3 space-y-4">
                <Field label={t("salary.dialog.bonusOptional")}>
                  <input
                    {...moneyInputProps}
                    className="input-base"
                    placeholder="0"
                    value={bonus}
                    onChange={(e) => setBonus(sanitizeMoney(e.target.value))}
                  />
                </Field>
                <Field
                  label={t("salary.dialog.vacationPay")}
                  hint={t("salary.dialog.vacationPayHint")}
                >
                  <input
                    {...moneyInputProps}
                    className="input-base"
                    placeholder="0"
                    value={vacation}
                    onChange={(e) => setVacation(sanitizeMoney(e.target.value))}
                  />
                </Field>
                <Field label={t("salary.dialog.benefits")} hint={t("salary.dialog.benefitsHint")}>
                  <input
                    {...moneyInputProps}
                    className="input-base"
                    placeholder="0"
                    value={benefits}
                    onChange={(e) => setBenefits(sanitizeMoney(e.target.value))}
                  />
                </Field>
                <Field label={t("salary.dialog.otherIncome")}>
                  <input
                    {...moneyInputProps}
                    className="input-base"
                    placeholder="0"
                    value={other}
                    onChange={(e) => setOther(sanitizeMoney(e.target.value))}
                  />
                </Field>
              </div>
            )}
          </div>

          <Field label={t("salary.dialog.notes")}>
            <textarea
              className="input-base"
              rows={2}
              maxLength={500}
              value={notes ?? ""}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
          {fieldError && (
            <div
              role="alert"
              aria-live="polite"
              className="text-[11px] text-[color:var(--color-negative)]"
            >
              {fieldError}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            {initial && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-3 rounded-md border border-border text-sm text-[color:var(--color-negative)] flex items-center gap-2"
              >
                <Trash2 className="size-4" strokeWidth={1.5} /> {t("common.delete")}
              </button>
            )}
            <button
              type="submit"
              disabled={upsert.isPending}
              className="flex-1 py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50"
            >
              {upsert.isPending
                ? t("common.saving")
                : initial
                  ? t("common.update")
                  : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

type MetricKey =
  | "totalEarned"
  | "avgSalary"
  | "cumGrowth"
  | "cagr"
  | "cumCpi"
  | "avgCpi"
  | "cumReal"
  | "avgReal"
  | "purchasingPower";

type RangeKey = "all" | "3" | "5" | "10" | "custom";

const METRIC_KEYS: MetricKey[] = [
  "totalEarned",
  "avgSalary",
  "cumGrowth",
  "cagr",
  "cumCpi",
  "avgCpi",
  "cumReal",
  "avgReal",
  "purchasingPower",
];

const RANGE_KEYS: RangeKey[] = ["all", "3", "5", "10", "custom"];

function StatsSection({ rows }: { rows: import("@/lib/queries").SalaryGrowthRow[] }) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<MetricKey>("totalEarned");
  const [range, setRange] = useState<RangeKey>("all");
  const allYears = rows.map((r) => r.year);
  const minY = allYears[0];
  const maxY = allYears[allYears.length - 1];
  const [fromYear, setFromYear] = useState<number>(minY);
  const [toYear, setToYear] = useState<number>(maxY);

  // resolve window
  let wFrom = minY;
  let wTo = maxY;
  if (range === "3") wFrom = Math.max(minY, maxY - 2);
  else if (range === "5") wFrom = Math.max(minY, maxY - 4);
  else if (range === "10") wFrom = Math.max(minY, maxY - 9);
  else if (range === "custom") {
    wFrom = Math.min(fromYear, toYear);
    wTo = Math.max(fromYear, toYear);
  }
  const windowRows = rows.filter((r) => r.year >= wFrom && r.year <= wTo);
  const n = windowRows.length;
  const first = windowRows[0];
  const last = windowRows[n - 1];

  let cumCpi: number | null = null;
  let windowUsedProvisional = false;
  if (n >= 2) {
    let f = 1;
    let any = false;
    for (let i = 1; i < n; i++) {
      const c = windowRows[i].inflationPct;
      if (c != null) {
        f *= 1 + c / 100;
        any = true;
      }
      if (windowRows[i].inflationKind === "tolvmanedersendring") windowUsedProvisional = true;
    }
    cumCpi = any ? (f - 1) * 100 : null;
  }
  const cumGrowth =
    n >= 2 && first.salary > 0 ? ((last.salary - first.salary) / first.salary) * 100 : null;
  const years = n - 1;
  const cagr =
    cumGrowth != null && years > 0 ? (Math.pow(1 + cumGrowth / 100, 1 / years) - 1) * 100 : null;
  const avgCpi =
    cumCpi != null && years > 0 ? (Math.pow(1 + cumCpi / 100, 1 / years) - 1) * 100 : null;
  const cumReal = cumGrowth != null && cumCpi != null ? realGrowth(cumGrowth, cumCpi) : null;
  const avgReal =
    cumReal != null && years > 0 ? (Math.pow(1 + cumReal / 100, 1 / years) - 1) * 100 : null;
  const total = windowRows.reduce((s, r) => s + r.salary, 0);
  const avgSal = n > 0 ? total / n : null;
  const purchasingPower = cumCpi != null && last ? last.salary / (1 + cumCpi / 100) : null;

  const isPct = metric !== "totalEarned" && metric !== "avgSalary" && metric !== "purchasingPower";
  const needsDelta = metric !== "totalEarned" && metric !== "avgSalary";

  let value: string = "—";
  if (!(needsDelta && n < 2)) {
    switch (metric) {
      case "totalEarned":
        value = formatNok(total);
        break;
      case "avgSalary":
        value = avgSal != null ? formatNok(avgSal) : "—";
        break;
      case "cumGrowth":
        value = cumGrowth != null ? formatPercent(cumGrowth, { signed: true }) : "—";
        break;
      case "cagr":
        value = cagr != null ? formatPercent(cagr, { signed: true }) : "—";
        break;
      case "cumCpi":
        value = cumCpi != null ? `${cumCpi.toFixed(1)}%` : "—";
        break;
      case "avgCpi":
        value = avgCpi != null ? `${avgCpi.toFixed(1)}%` : "—";
        break;
      case "cumReal":
        value = cumReal != null ? formatPercent(cumReal, { signed: true }) : "—";
        break;
      case "avgReal":
        value = avgReal != null ? formatPercent(avgReal, { signed: true }) : "—";
        break;
      case "purchasingPower":
        value = purchasingPower != null ? formatNok(purchasingPower) : "—";
        break;
    }
  }

  const numericTone: "pos" | "neg" | undefined = (() => {
    if (!isPct) return undefined;
    const map: Record<string, number | null> = {
      cumGrowth: cumGrowth ?? null,
      cagr: cagr ?? null,
      cumReal: cumReal ?? null,
      avgReal: avgReal ?? null,
    };
    const v = map[metric];
    if (v == null) return undefined;
    return v >= 0 ? "pos" : "neg";
  })();

  const span =
    first && last ? `${first.year}–${last.year} · ${t("salary.yearsSuffix", { count: n })}` : "";
  const helper =
    needsDelta && n < 2 ? t("salary.needTwoYears") : t(`salary.metrics.${metric}Help` as const);

  return (
    <section className="animate-entry [animation-delay:150ms]">
      <h2 className="font-display font-bold text-sm tracking-tight mb-4">
        {t("salary.statsTitle")}
      </h2>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="salary-metric"
            className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
          >
            {t("salary.statsMetric")}
          </label>
          <select
            id="salary-metric"
            value={metric}
            onChange={(e) => setMetric(e.target.value as MetricKey)}
            className="w-full text-[11px] font-mono uppercase tracking-widest border border-border rounded-full bg-transparent px-3 py-1.5 text-muted-foreground hover:text-foreground truncate"
          >
            {METRIC_KEYS.map((k) => (
              <option key={k} value={k}>
                {t(`salary.metrics.${k}` as const)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="salary-range"
            className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
          >
            {t("salary.statsRange")}
          </label>
          <select
            id="salary-range"
            value={range}
            onChange={(e) => setRange(e.target.value as RangeKey)}
            className="w-full text-[11px] font-mono uppercase tracking-widest border border-border rounded-full bg-transparent px-3 py-1.5 text-muted-foreground hover:text-foreground truncate"
          >
            {RANGE_KEYS.map((k) => (
              <option key={k} value={k}>
                {t(
                  `salary.range${k === "all" ? "All" : k === "custom" ? "Custom" : `Last${k}`}` as const,
                )}
              </option>
            ))}
          </select>
        </div>
      </div>

      {range === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              {t("salary.statsFrom")}
            </span>
            <select
              className="input-base"
              value={fromYear}
              onChange={(e) => setFromYear(Number(e.target.value))}
            >
              {allYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              {t("salary.statsTo")}
            </span>
            <select
              className="input-base"
              value={toYear}
              onChange={(e) => setToYear(Number(e.target.value))}
            >
              {allYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="border-y border-border py-4">
        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1 flex-wrap">
          <span>
            {t(`salary.metrics.${metric}` as const)}
            {span ? ` · ${span}` : ""}
          </span>
          <InfoTooltip
            text={t(`salary.statsMetricHelp_${metric}` as const)}
            label={t(`salary.metrics.${metric}` as const)}
          />
        </div>
        <div
          className={
            "text-2xl font-display font-bold tabular-nums flex items-baseline gap-2 " +
            (numericTone === "pos"
              ? "text-[color:var(--color-positive)]"
              : numericTone === "neg"
                ? "text-[color:var(--color-negative)]"
                : "")
          }
        >
          {value}
          {windowUsedProvisional && metric !== "totalEarned" && metric !== "avgSalary" && (
            <span className="inline-flex items-center gap-1 text-[9px] font-mono font-normal normal-case text-muted-foreground border border-border rounded px-1 py-0.5">
              {t("dashboard.cpiProvisional")}
              <InfoTooltip
                text={t("salary.cpiProvisionalHelp")}
                label={t("dashboard.cpiProvisional")}
              />
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">{helper}</p>
      </div>
    </section>
  );
}
